import { Button, Flex, useStyles } from '@archipro-design/aria';
import { HeartFill, HeartLine } from '@archipro-design/icons';
import * as S from './VariantPOA.mobile.style';
import { FollowSelector } from '~/modules/moodboards/components';
import type { PrimaryCTAResult } from '~/modules/professional/util/professional-cta-helper';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import { WhereToBuyButton } from '../product-details/WhereToBuyButton';
import { useVariantSelectionState } from '../../context/VariantSelectionContext';
import { useSampleStock } from '../../hook/use-sample-stock';
import type Product from '@modules/product/type/product';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

interface VariantPOAProps {
    itemID: number;
    professional: {
        title: string;
        id: number;
    };
    primaryCTA?: PrimaryCTAResult;
    isInServiceableArea: boolean;
    whereToBuyEnabled?: boolean;
    onClickWhereToBuy?: () => void;
    /** Callback to open the enquiry modal. */
    onEnquire?: () => void;
    product: Product;
}

const VariantPOA = (props: VariantPOAProps) => {
    const {
        itemID,
        primaryCTA,
        isInServiceableArea,
        whereToBuyEnabled,
        onClickWhereToBuy,
        onEnquire,
        product,
    } = props;
    const favouriteItem = useFavouriteVariant(itemID);
    const { css } = useStyles();
    const { isInDesignBoard } = useHandleFollow();
    const { selectedVariant } = useVariantSelectionState();
    const { canPurchase: sampleCanPurchase } = useSampleStock(selectedVariant);
    const hasShippingMethods = selectedVariant?.SampleShippingProfile
        ?.NoShipping
        ? false
        : (selectedVariant?.SampleShippingProfile?.ShippingMethods ?? [])
              .length > 0;
    const pickupLocations = product.Professional.PickupLocations;
    const hasSampleShippingOptions =
        hasShippingMethods || pickupLocations.length > 0;

    return (
        <>
            <div className={css(S.Container)}>
                {isInServiceableArea ? (
                    <div className={css(S.Wrapper)}>
                        <Button
                            size={16}
                            fluid
                            variables={{
                                iconPadding: 0,
                                buttonPadding: 0,
                            }}
                            onClick={() => onEnquire?.()}
                        >
                            {(
                                primaryCTA?.primaryCTALabel ?? `REQUEST PRICE`
                            ).toUpperCase()}
                        </Button>
                    </div>
                ) : (
                    <div></div>
                )}
                {isInServiceableArea && (
                    <Flex gap="space-20" className={css(S.SaveShareContainer)}>
                        <FollowSelector
                            trigger={
                                <Button
                                    className={css(S.ButtonHeart)}
                                    transparent
                                    size={20}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                    icon={
                                        isInDesignBoard(
                                            favouriteItem.pinId,
                                            favouriteItem.pinType,
                                        ) ? (
                                            <HeartFill size="2x" />
                                        ) : (
                                            <HeartLine size="2x" />
                                        )
                                    }
                                    variables={{
                                        iconPadding: 0,
                                        buttonPadding: 0,
                                    }}
                                />
                            }
                            pinId={favouriteItem.pinId}
                            pinType={favouriteItem.pinType}
                        />
                    </Flex>
                )}
            </div>

            {whereToBuyEnabled &&
                (!sampleCanPurchase || !hasSampleShippingOptions) && (
                    <Flex>
                        <WhereToBuyButton onClick={onClickWhereToBuy} />
                    </Flex>
                )}
        </>
    );
};

export default VariantPOA;
